import { useEffect, useRef, useState } from 'react';

import { mapBridge } from './mapBridge';

export function LayersControl({ vesselCount = 0 }: { vesselCount?: number }) {
  const [open, setOpen] = useState(false);
  const [topography, setTopography] = useState(true);
  const [heightHolograms, setHeightHolograms] = useState(true);
  const [aircraft3DModels, setAircraft3DModels] = useState(true);
  const [groundIcon, setGroundIcon] = useState(true);
  const [trajectoryLines, setTrajectoryLines] = useState(true);
  const [trajectoryFrom3D, setTrajectoryFrom3D] = useState(true);
  const [trajectorySourceLocked, setTrajectorySourceLocked] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  const syncTrajectorySource = () => {
    setTrajectoryFrom3D(mapBridge.getEffectiveTrajectoryFrom3D());
    setTrajectorySourceLocked(!mapBridge.isTrajectorySourceToggleEnabled());
  };

  useEffect(() => mapBridge.subscribeTopography(setTopography), []);
  useEffect(() => mapBridge.subscribeHeightHolograms(setHeightHolograms), []);
  useEffect(() => mapBridge.subscribeAircraft3DModels(setAircraft3DModels), []);
  useEffect(() => mapBridge.subscribeGroundIcon(setGroundIcon), []);
  useEffect(() => mapBridge.subscribeTrajectoryLines(setTrajectoryLines), []);
  useEffect(() => mapBridge.subscribeTrajectorySource(syncTrajectorySource), []);
  useEffect(() => mapBridge.subscribePitch(() => syncTrajectorySource()), []);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex h-9 w-9 items-center justify-center rounded-lg bg-bg-panel/90 text-[9px] font-semibold leading-tight tracking-tight text-slate-100 shadow ring-1 ring-bg-border backdrop-blur hover:bg-white/5 active:bg-white/10 ${
          open ? 'bg-white/10 ring-accent/60' : ''
        }`}
        aria-label="Map layers"
        aria-expanded={open}
        title="Map layers"
      >
        Layers
      </button>

      {open ? (
        <div
          className="absolute left-[calc(100%+8px)] top-0 z-40 min-w-[11.5rem] rounded-lg bg-bg-panel/95 p-2 shadow-lg ring-1 ring-bg-border backdrop-blur"
          role="menu"
        >
          <div className="mb-1 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            Map layers
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-xs text-slate-200 hover:bg-white/5">
            <input
              type="checkbox"
              checked={topography}
              onChange={(e) => mapBridge.setTopographyEnabled(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-bg-border bg-bg-raised text-accent focus:ring-accent/40"
            />
            Topography
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-xs text-slate-200 hover:bg-white/5">
            <input
              type="checkbox"
              checked={trajectoryLines}
              onChange={(e) => mapBridge.setTrajectoryLinesEnabled(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-bg-border bg-bg-raised text-accent focus:ring-accent/40"
            />
            Trajectory lines
          </label>
          <div className="mb-1 mt-2 px-1.5 text-[10px] font-semibold uppercase tracking-wide text-slate-500">
            3D layers
          </div>
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-xs text-slate-200 hover:bg-white/5">
            <input
              type="checkbox"
              checked={groundIcon}
              onChange={(e) => mapBridge.setGroundIconEnabled(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-bg-border bg-bg-raised text-accent focus:ring-accent/40"
            />
            Ground icon
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-xs text-slate-200 hover:bg-white/5">
            <input
              type="checkbox"
              checked={heightHolograms}
              onChange={(e) => mapBridge.setHeightHologramsEnabled(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-bg-border bg-bg-raised text-accent focus:ring-accent/40"
            />
            Height holograms
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-md px-1.5 py-1.5 text-xs text-slate-200 hover:bg-white/5">
            <input
              type="checkbox"
              checked={aircraft3DModels}
              onChange={(e) => mapBridge.setAircraft3DModelsEnabled(e.target.checked)}
              className="h-3.5 w-3.5 rounded border-bg-border bg-bg-raised text-accent focus:ring-accent/40"
            />
            3D aircraft models
          </label>
          <div
            className={`rounded-md px-1.5 py-1.5 ${
              trajectorySourceLocked || !trajectoryLines ? 'opacity-50' : ''
            }`}
            title={
              !trajectoryLines
                ? 'Enable trajectory lines first'
                : trajectorySourceLocked
                  ? mapBridge.getTrajectorySourceOverride() === 'ground-off'
                    ? 'Ground icon off — trajectories follow 3D aircraft'
                    : mapBridge.getTrajectorySourceOverride() === 'models-off'
                      ? '3D models off — trajectories follow ground icon'
                      : 'Only available in 3D view'
                  : 'Choose where trajectory lines start from'
            }
          >
            <div className="mb-1.5 text-[10px] font-medium text-slate-400">Trajectory source</div>
            <div
              role="group"
              aria-label="Trajectory source"
              className="relative grid grid-cols-2 rounded-md bg-bg-raised p-0.5 ring-1 ring-bg-border"
            >
              <button
                type="button"
                disabled={trajectorySourceLocked || !trajectoryLines}
                onClick={() => mapBridge.setTrajectoryFrom3DAircraft(false)}
                className={`rounded px-1.5 py-1 text-[10px] font-medium transition disabled:cursor-not-allowed ${
                  !trajectoryFrom3D
                    ? 'bg-white/10 text-slate-100 shadow-sm ring-1 ring-white/10'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                Ground icon
              </button>
              <button
                type="button"
                disabled={trajectorySourceLocked || !trajectoryLines}
                onClick={() => mapBridge.setTrajectoryFrom3DAircraft(true)}
                className={`rounded px-1.5 py-1 text-[10px] font-medium transition disabled:cursor-not-allowed ${
                  trajectoryFrom3D
                    ? 'bg-white/10 text-slate-100 shadow-sm ring-1 ring-white/10'
                    : 'text-slate-500 hover:text-slate-300'
                }`}
              >
                3D aircraft
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
